import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { folderDisplayString } from "@components/folderWithSubfoldersString";
import { knolToFSRSCard } from "@core/fsrs/scheduler";
import { AlertOptions, IonicSafeString } from "@ionic/react";
import { isDeckFSRS } from "@models/deckSettings";
import logEvent from "analytics";
import { SiriShortcuts } from "capacitor-plugin-siri-shorts";
import L10n from "localization";
import { ID } from "types/ID";
import { superCache } from "../../../hooks/data/superCache";

// Helper function to calculate user's average review time
function getUserAverageReviewTime(): number {
  if (!superCache.responses || !superCache.responsesLoaded) {
    return 0;
  }

  let totalDuration = 0;
  let totalResponses = 0;

  // Get responses from the last 30 days to calculate a recent average
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

  for (const [, layoutMap] of superCache.responses) {
    for (const [, responses] of layoutMap) {
      for (const response of responses) {
        // Only count responses from the last 30 days
        if (response.createdAt >= thirtyDaysAgo) {
          totalDuration += response.durationMillis;
          totalResponses++;
        }
      }
    }
  }

  // Return average time in seconds, or 0 if no responses
  return totalResponses > 0 ? Math.round(totalDuration / totalResponses / 1000) : 0;
}

// Helper function to format time estimate
function formatTimeEstimate(seconds: number): string {
  if (seconds <= 0) return "";

  const minutes = Math.round(seconds / 60);

  if (minutes < 1) {
    return `${seconds}s`;
  }
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
}

export async function scheduleStudyReminderNotification(
  deckName: string,
  isFSRS: boolean,
  presentAlert: (options: AlertOptions) => Promise<void>,
) {
  if (!deckName) {
    return;
  }

  try {
    // Check permissions first
    const permissionStatus = await LocalNotifications.checkPermissions();

    if (permissionStatus.display === "prompt") {
      // Show alert before requesting permissions if presentAlert is provided
      await new Promise<void>((resolve) => {
        presentAlert({
          message: new IonicSafeString(`
            <div style="text-align: center;">
              <img src="/reminders-icon.png" style="width: 120px; height: 120px;" />
              <div>${L10n.localize((s) => s.onboarding.notifPermissionRequestWarmup)}</div>
            </div>
          `),
          buttons: [
            {
              text: L10n.localize((s) => s.actions.ok),
              handler: () => {
                resolve();
              },
            },
          ],
          backdropDismiss: false,
          keyboardClose: false,
        });
      });

      // Request permissions after alert is dismissed
      const requestResult = await LocalNotifications.requestPermissions();
      const granted = requestResult.display === "granted";

      // Log analytics event
      logEvent("requested_notif_permissions", { granted });

      if (!granted) {
        console.warn("Notification permissions denied");
        return;
      }
    } else if (permissionStatus.display !== "granted") {
      // Permissions were previously denied, don't ask again
      console.warn("Notification permissions were previously denied");
      return;
    }

    const displayName = folderDisplayString(deckName);
    const now = new Date();

    // Cancel all existing notifications
    await LocalNotifications.cancel({
      notifications: await LocalNotifications.getPending().then((r) => r.notifications),
    });

    // Helper function to get FSRS due card count by a specific future time
    const getFSRSDueCardCount = (targetTime: number): number => {
      if (!isFSRS || !superCache.decks || !superCache.knols) {
        return 0;
      }

      // Find all FSRS decks
      const fsrsDeckIDs = new Set<ID>();
      for (const [id, deck] of superCache.decks.entries()) {
        if (isDeckFSRS(deck)) {
          fsrsDeckIDs.add(id);
        }
      }

      if (fsrsDeckIDs.size === 0) {
        return 0;
      }

      // Count cards that will be due by the target time
      let dueCount = 0;
      for (const [, knol] of superCache.knols.entries()) {
        if (fsrsDeckIDs.has(knol.deck_id)) {
          try {
            const fsrsCard = knolToFSRSCard(knol, Date.now());
            if (fsrsCard && fsrsCard.state !== "New" && fsrsCard.due <= targetTime) {
              dueCount++;
            }
          } catch (error) {
            // Silently ignore conversion errors
          }
        }
      }

      return dueCount;
    };

    // Get user's average review time for time estimates
    const avgReviewTimeSeconds = getUserAverageReviewTime();

    const notifications = [];

    // 1 day notification
    const dayTime = now.getTime() + (1 * 24 + 0.5) * 60 * 60 * 1000;
    const dayDueCount = isFSRS ? getFSRSDueCardCount(dayTime) : 0;

    if (!isFSRS || dayDueCount > 0) {
      let dayBody: string;
      if (isFSRS && dayDueCount > 0) {
        const timeEstimate = formatTimeEstimate(dayDueCount * avgReviewTimeSeconds);
        dayBody = timeEstimate
          ? L10n.localize((s) => s.funcs.general.studyNotificationDue1DayWithTime)(
              dayDueCount,
              timeEstimate,
            )
          : L10n.localize((s) => s.funcs.general.studyNotificationDue1Day)(dayDueCount);
      } else {
        dayBody = L10n.localize((s) => s.funcs.general.studyNotification)(displayName);
      }

      notifications.push({
        title: L10n.localize((s) => s.review.singular),
        body: dayBody,
        id: 1,
        schedule: { at: new Date(dayTime) },
      });
    }

    // 3 days notification
    const threeDayTime = now.getTime() + (3 * 24 + 0.5) * 60 * 60 * 1000;
    const threeDayDueCount = isFSRS ? getFSRSDueCardCount(threeDayTime) : 0;

    if (!isFSRS || threeDayDueCount > 0) {
      let threeDayBody: string;
      if (isFSRS && threeDayDueCount > 0) {
        const timeEstimate = formatTimeEstimate(threeDayDueCount * avgReviewTimeSeconds);
        threeDayBody = timeEstimate
          ? L10n.localize((s) => s.funcs.general.studyNotificationDue3DaysWithTime)(
              threeDayDueCount,
              timeEstimate,
            )
          : L10n.localize((s) => s.funcs.general.studyNotificationDue3Days)(threeDayDueCount);
      } else {
        threeDayBody = L10n.localize((s) => s.funcs.general.studyNotification3Days)(displayName);
      }

      notifications.push({
        title: L10n.localize((s) => s.review.singular),
        body: threeDayBody,
        id: 2,
        schedule: { at: new Date(threeDayTime) },
      });
    }

    // 1 week notification
    const weekTime = now.getTime() + (7 * 24 + 0.5) * 60 * 60 * 1000;
    const weekDueCount = isFSRS ? getFSRSDueCardCount(weekTime) : 0;

    if (!isFSRS || weekDueCount > 0) {
      let weekBody: string;
      if (isFSRS && weekDueCount > 0) {
        const timeEstimate = formatTimeEstimate(weekDueCount * avgReviewTimeSeconds);
        weekBody = timeEstimate
          ? L10n.localize((s) => s.funcs.general.studyNotificationDue1WeekWithTime)(
              weekDueCount,
              timeEstimate,
            )
          : L10n.localize((s) => s.funcs.general.studyNotificationDue1Week)(weekDueCount);
      } else {
        weekBody = L10n.localize((s) => s.funcs.general.studyNotification1Week)(displayName);
      }

      notifications.push({
        title: L10n.localize((s) => s.review.singular),
        body: weekBody,
        id: 3,
        schedule: { at: new Date(weekTime) },
      });
    }

    if (notifications.length > 0) {
      await LocalNotifications.schedule({ notifications });
    }
  } catch (error) {
    console.warn("Failed to schedule notifications:", error);
  }
}

export async function informSiriAboutStudy(
  deckTagName: string | undefined,
  deckName: string,
  deckID: ID | undefined,
) {
  if (!Capacitor.isPluginAvailable("SiriShortcuts")) {
    return;
  }

  const displayName = folderDisplayString(deckName);
  const name = deckTagName
    ? `${L10n.localize((s) => s.folder.singular)} ${deckTagName}`
    : displayName;

  try {
    await SiriShortcuts.donate({
      persistentIdentifier: deckTagName ? `review-group-${deckTagName}` : `review-deck-${deckID}`,
      title: `${L10n.localize((s) => s.review.singular)} ${name}`,
      suggestedInvocationPhrase: `${L10n.localize((s) => s.review.singular)} ${name}`,
      userInfo: {
        ...(deckID !== undefined ? { deckId: deckID } : {}),
        ...(deckTagName ? { deckTagName: deckTagName } : {}),
      },
      isEligibleForSearch: true,
      isEligibleForPrediction: true,
    });
  } catch (e) {
    console.warn("Failed to inform Siri about study session:", e);
  }
}
